import React, { useState, useEffect, useMemo } from 'react';
import {
  List,
  useNotify,
  Loading
} from 'react-admin';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  TablePagination
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS } from 'date-fns/locale';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { apiUrl } from '../../config/api';
import { httpClient } from '../../utils/httpClient';

interface MAUDAUData {
  mau: number;
  dau: number;
  prev_dau?: number;
  prev_mau?: number;
}

interface MAUDAUTrendItem {
  date: string;
  mau?: number;
  dau?: number;
  active_users?: number;
}

interface EngagementMetrics {
  avg_duration: number;
  completion_rate: number;
}

interface RetentionCohort {
  cohort: string;
  day_0: number;
  day_1: number;
  day_7: number;
  day_14: number;
  day_30: number;
}

interface FunnelMetrics {
  left_email: { count: number; percentage: number };
  confirmed_email: { count: number; percentage: number };
  logged_in: { count: number; percentage: number };
  started_listening: { count: number; percentage: number };
}

interface EngagementAnalyticsResponse {
  success: boolean;
  data: {
    mau_dau: MAUDAUData;
    mau_dau_trend: MAUDAUTrendItem[];
    engagement: EngagementMetrics;
    retention_cohorts: RetentionCohort[];
    avg_session_length: number;
    funnel: FunnelMetrics;
    retention_next: {
      next_day: number;
      next_week: number;
    };
  };
}

interface UserStats {
  registered_users_count: number;
  waitlist_users_count: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  created_at: string;
  is_verified?: boolean;
}

interface UsersResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

const UserAnalyticsList = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const notify = useNotify();
  
  // Date range for Active Users chart
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29); // Last 30 days
    return { start, end };
  };

  const defaultDates = getDefaultDates();
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Temporary dates (user input, not yet applied)
  const [tempStartDate, setTempStartDate] = useState<Date | null>(defaultDates.start);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(defaultDates.end);
  // Applied dates (used for API requests) - initialize with default dates
  const [chartStartDate, setChartStartDate] = useState<string>(formatDateLocal(defaultDates.start));
  const [chartEndDate, setChartEndDate] = useState<string>(formatDateLocal(defaultDates.end));
  const [useCustomDateRange, setUseCustomDateRange] = useState<boolean>(true); // Start with custom range enabled
  // Selected quick period button (for visual indication)
  const [selectedQuickPeriod, setSelectedQuickPeriod] = useState<string | null>(null);

  const funnelDefaultDates = getDefaultDates();

  const [funnelTempStartDate, setFunnelTempStartDate] = useState<Date | null>(funnelDefaultDates.start);
  const [funnelTempEndDate, setFunnelTempEndDate] = useState<Date | null>(funnelDefaultDates.end);

  const [funnelStartDate, setFunnelStartDate] = useState<string>(formatDateLocal(funnelDefaultDates.start));
  const [funnelEndDate, setFunnelEndDate] = useState<string>(formatDateLocal(funnelDefaultDates.end));
  const [useFunnelCustomDateRange, setUseFunnelCustomDateRange] = useState<boolean>(true); // Start with custom range enabled

  const [selectedFunnelQuickPeriod, setSelectedFunnelQuickPeriod] = useState<string | null>(null);
 
  const [mauDau, setMauDau] = useState<MAUDAUData>({ mau: 0, dau: 0 });
  const [mauDauTrend, setMauDauTrend] = useState<MAUDAUTrendItem[]>([]);
  const [engagement, setEngagement] = useState<EngagementMetrics>({ avg_duration: 0, completion_rate: 0 });
  const [retentionCohorts, setRetentionCohorts] = useState<RetentionCohort[]>([]);
  const [avgSessionLength, setAvgSessionLength] = useState<number>(0);
  const [funnel, setFunnel] = useState<FunnelMetrics | null>(null);
  
  // User list tab states
  const [userStats, setUserStats] = useState<UserStats>({ registered_users_count: 0, waitlist_users_count: 0 });
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState<boolean>(true);
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(20);
  const [usersTotal, setUsersTotal] = useState(0);

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.append('period', 'month');
        
        // For Active Users chart: if custom date range is used, pass dates instead of period
        if (useCustomDateRange && chartStartDate && chartEndDate) {
          params.append('chart_start_date', chartStartDate);
          params.append('chart_end_date', chartEndDate);
        }

        if (useFunnelCustomDateRange && funnelStartDate && funnelEndDate) {
          params.append('funnel_start_date', funnelStartDate);
          params.append('funnel_end_date', funnelEndDate);
        }

        const url = `${apiUrl}/admin/analytics/engagement?${params.toString()}`;
        const response = await httpClient(url);

        if (response.json.success) {
          const data = response.json.data;
          setMauDau(data.mau_dau);
          setMauDauTrend(data.mau_dau_trend);
          setEngagement(data.engagement);
          setRetentionCohorts(data.retention_cohorts);
          setAvgSessionLength(data.avg_session_length);
          setFunnel(data.funnel);
        } else {
          throw new Error(response.json.message || 'Failed to load analytics');
        }
      } catch (err: any) {
        const errorMessage = (err instanceof Error && err.message) ? err.message : 'Failed to load analytics';
        setError(errorMessage);
        notify(errorMessage, { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [notify, useCustomDateRange, chartStartDate, chartEndDate, useFunnelCustomDateRange, funnelStartDate, funnelEndDate]);

  // Load user stats when tab list of users is opened
  useEffect(() => {
    if (tabValue === 2) {
      const loadUserStats = async () => {
        try {
          const url = `${apiUrl}/admin/users/stats`;
          const response = await httpClient(url);
          
          if (response.json.success) {
            setUserStats(response.json.data);
          }
        } catch (err: any) {
          console.error('Failed to load user stats:', err);
        }
      };
      
      loadUserStats();
    }
  }, [tabValue]);

  // Load users list when tab list of users is opened or filter changes
  useEffect(() => {
    if (tabValue === 2) {
      const loadUsers = async () => {
        setUsersLoading(true);
        try {
          const params = new URLSearchParams();
          params.append('registered', isRegistered.toString());
          params.append('page', (usersPage + 1).toString()); // Convert 0-based to 1-based for API
          params.append('limit', usersRowsPerPage.toString());
          
          const url = `${apiUrl}/admin/users?${params.toString()}`;
          const response = await httpClient(url);
          
          if (response.json.success) {
            const data = response.json.data as UsersResponse['data'];
            setUsers(data.users);
            setUsersTotal(data.pagination.total);
          } else {
            throw new Error(response.json.message || 'Failed to load users');
          }
        } catch (err: any) {
          const errorMessage = (err instanceof Error && err.message) ? err.message : 'Failed to load users';
          notify(errorMessage, { type: 'error' });
        } finally {
          setUsersLoading(false);
        }
      };
      
      loadUsers();
    }
  }, [tabValue, isRegistered, usersPage, usersRowsPerPage, notify]);

  // Reset users page when switching tabs
  useEffect(() => {
    if (tabValue !== 2) {
      setUsersPage(0);
    }
  }, [tabValue]);

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.floor((minutes % 1) * 60);
    if (hours > 0) {
      return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const calculateChange = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 1000) / 10;
  };

  // DAU: compare today with yesterday
  const dauChange = calculateChange(mauDau.dau, mauDau.prev_dau || 0);
  const isDauUp = mauDau.dau >= (mauDau.prev_dau || 0);

  // MAU: compare last 30 days with previous 30 days
  const mauChange = calculateChange(mauDau.mau, mauDau.prev_mau || 0);
  const isMauUp = mauDau.mau >= (mauDau.prev_mau || 0);

  const getRetentionColor = (value: number): string => {
    if (value >= 60) return '#c8e6c9'; // Light green
    if (value >= 40) return '#fff9c4'; // Light yellow
    if (value >= 25) return '#ffccbc'; // Light orange
    return '#ffcdd2'; // Light pink
  };

  const funnelData = funnel ? [
    {
      name: 'Left Email',
      value: funnel.left_email?.count || 0,
      percentage: funnel.left_email?.percentage || 0
    },
    {
      name: 'Confirmed Email',
      value: funnel.confirmed_email?.count || 0,
      percentage: funnel.confirmed_email?.percentage || 0
    },
    {
      name: 'Logged In',
      value: funnel.logged_in?.count || 0,
      percentage: funnel.logged_in?.percentage || 0
    },
    {
      name: 'Started Listening',
      value: funnel.started_listening?.count || 0,
      percentage: funnel.started_listening?.percentage || 0
    }
  ] : [];

  // Process MAU/DAU trend data based on selected period or custom date range
  // active_users = DAU (Daily Active Users) for each day
  const processedTrend = useMemo(() => {
    if (mauDauTrend.length === 0) return [];

    const overallMAU = mauDau.mau || 0;

    const shouldShowMAU = useCustomDateRange 
      ? (chartStartDate && chartEndDate && (new Date(chartEndDate).getTime() - new Date(chartStartDate).getTime()) >= 7 * 24 * 60 * 60 * 1000)
      : (mauDauTrend.length >= 7); // Default month period shows 30 days, so show MAU
    
    return mauDauTrend.map((item) => {
      // Ensure we're working with numbers, not strings or decimals
      const activeUsers = typeof item.active_users === 'number' 
        ? Math.round(item.active_users) 
        : Math.round(parseFloat(String(item.active_users || 0)) || 0);
      
      // active_users is now DAU for each day
      return {
        date: item.date,
        mau: shouldShowMAU ? overallMAU : undefined,
        dau: activeUsers // DAU for each day
      };
    });
  }, [mauDauTrend, mauDau, useCustomDateRange, chartStartDate, chartEndDate]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box sx={{ padding: 2 }}>
        <Paper sx={{ padding: 2 }}>
          <Typography color="error">Error: {error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <List title="User Engagement Analytics" empty={false} disableSyncWithLocation exporter={false}>
      <Box sx={{ padding: 2 }}>
        {/* Header with Tabs */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, flexWrap: 'wrap', gap: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ flexGrow: 1, minWidth: 300 }}
          >
            <Tab label="Active Users" />
            <Tab label="Retention & Conversion" />
            <Tab label="List of users" />
          </Tabs>
        </Box>

        {/* Tab Panel 1: Active Users (KPI Cards + MAU/DAU Chart) */}
        {tabValue === 0 && (
          <Box>
            {/* KPI Cards - Only DAU and MAU */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, marginBottom: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      DAU
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: 1 }}>
                      {mauDau.dau.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isDauUp ? (
                        <TrendingUp sx={{ color: '#2e7d32', fontSize: 16 }} />
                      ) : (
                        <TrendingDown sx={{ color: '#d32f2f', fontSize: 16 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: isDauUp ? '#2e7d32' : '#d32f2f', 
                          fontWeight: 500 
                        }}
                      >
                        {Math.abs(dauChange)}% vs yesterday
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                      Daily Active Users
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      MAU
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: 1 }}>
                      {mauDau.mau.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isMauUp ? (
                        <TrendingUp sx={{ color: '#2e7d32', fontSize: 16 }} />
                      ) : (
                        <TrendingDown sx={{ color: '#d32f2f', fontSize: 16 }} />
                      )}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: isMauUp ? '#2e7d32' : '#d32f2f', 
                          fontWeight: 500 
                        }}
                      >
                        {Math.abs(mauChange)}% vs previous 30 days
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                      Monthly Active Users (Last 30 days)
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Date Range Selector for Chart - Above the chart */}
            <Paper sx={{ padding: 2, marginBottom: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap' }}>
                {/* Quick period buttons: 7d, 30d, 45d */}
                <ToggleButtonGroup
                  value={selectedQuickPeriod}
                  exclusive
                  onChange={(e, value) => {
                    if (value !== null) {
                      setSelectedQuickPeriod(value);
                      const end = new Date();
                      const start = new Date();
                      if (value === '7d') {
                        start.setDate(end.getDate() - 6);
                      } else if (value === '30d') {
                        start.setDate(end.getDate() - 29);
                      } else if (value === '45d') {
                        start.setDate(end.getDate() - 44);
                      }
                      setTempStartDate(start);
                      setTempEndDate(end);
                    } else {
                      setSelectedQuickPeriod(null);
                    }
                  }}
                  aria-label="quick period selection"
                >
                  <ToggleButton value="7d" aria-label="7 days">
                    7d
                  </ToggleButton>
                  <ToggleButton value="30d" aria-label="30 days">
                    30d
                  </ToggleButton>
                  <ToggleButton value="45d" aria-label="45 days">
                    45d
                  </ToggleButton>
                </ToggleButtonGroup>
                
                {/* Calendar date picker */}
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <DatePicker
                      label="Start Date"
                      value={tempStartDate}
                      onChange={(newValue) => {
                        setTempStartDate(newValue);
                        // Clear quick period selection when manually changing dates
                        setSelectedQuickPeriod(null);
                        // Validate: if end date exists and new start > end, show error
                        if (tempEndDate && newValue && newValue > tempEndDate) {
                          notify('Start date must be before end date', { type: 'warning' });
                        }
                      }}
                      slotProps={{
                        textField: {
                          sx: { minWidth: 150 },
                          error: !!(tempStartDate && tempEndDate && tempStartDate > tempEndDate)
                        }
                      }}
                    />
                    <Typography>—</Typography>
                    <DatePicker
                      label="End Date"
                      value={tempEndDate}
                      onChange={(newValue) => {
                        setTempEndDate(newValue);
                        // Clear quick period selection when manually changing dates
                        setSelectedQuickPeriod(null);
                        // Validate: if start date exists and start > new end, show error
                        if (tempStartDate && newValue && tempStartDate > newValue) {
                          notify('Start date must be before end date', { type: 'warning' });
                        }
                      }}
                      slotProps={{
                        textField: {
                          sx: { minWidth: 150 },
                          error: !!(tempStartDate && tempEndDate && tempStartDate > tempEndDate)
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (!tempStartDate || !tempEndDate) {
                          notify('Please select both start and end dates', { type: 'warning' });
                          return;
                        }
                        if (tempStartDate > tempEndDate) {
                          notify('Start date must be before end date', { type: 'warning' });
                          return;
                        }
                        // Apply the dates - convert Date to string using local timezone
                        const formatDateLocal = (date: Date): string => {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          return `${year}-${month}-${day}`;
                        };
                        const startDateStr = formatDateLocal(tempStartDate);
                        const endDateStr = formatDateLocal(tempEndDate);
                        setChartStartDate(startDateStr);
                        setChartEndDate(endDateStr);
                        setUseCustomDateRange(true);
                        // Keep temp dates in sync so user can modify and apply again
                      }}
                      disabled={!tempStartDate || !tempEndDate || !!(tempStartDate && tempEndDate && tempStartDate > tempEndDate)}
                    >
                      Apply
                    </Button>
                    
                    {useCustomDateRange && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setUseCustomDateRange(false);
                          setChartStartDate('');
                          setChartEndDate('');
                          setTempStartDate(null);
                          setTempEndDate(null);
                          setSelectedQuickPeriod(null);
                        }}
                      >
                        Reset
                      </Button>
                    )}
                  </Box>
                </LocalizationProvider>
              </Box>
            </Paper>

            {/* MAU/DAU Trend Chart */}
            <Paper sx={{ padding: 2, marginBottom: 3 }}>
              <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Active Users
              </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={processedTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getDate()} ${date.toLocaleDateString('en-US', { month: 'short' })}`;
                }}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={[0, (dataMax: number) => Math.max(Math.ceil(dataMax * 1.1), 1)]}
                tickFormatter={(value) => Math.round(value).toLocaleString()}
                allowDecimals={false}
              />
              <Tooltip 
                labelFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                }}
                formatter={(value: any) => value.toLocaleString()}
              />
              <Legend 
                iconType="circle"
                wrapperStyle={{ paddingTop: '20px' }}
              />
              {/* Show MAU line if custom date range with >= 7 days OR default month period */}
              {((useCustomDateRange && chartStartDate && chartEndDate && 
                 (new Date(chartEndDate).getTime() - new Date(chartStartDate).getTime()) >= 7 * 24 * 60 * 60 * 1000) ||
                (!useCustomDateRange && mauDauTrend.length >= 7)) && (
                <Line 
                  type="monotone" 
                  dataKey="mau" 
                  stroke="#1976d2" 
                  strokeWidth={2}
                  name="MAU"
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              )}
              <Line 
                type="monotone" 
                dataKey="dau" 
                stroke="#d32f2f" 
                strokeWidth={2}
                name="DAU"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
          </Box>
        )}

        {/* Tab Panel 2: Retention & Conversion Funnel */}
        {tabValue === 1 && (
          <Box>
            {/* Retention Table and Funnel Chart */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Retention Table */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
            <Paper sx={{ padding: 2 }}>
              <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Retention (User Retention)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Cohort</strong></TableCell>
                      <TableCell align="right"><strong>Day 0</strong></TableCell>
                      <TableCell align="right"><strong>Day 1</strong></TableCell>
                      <TableCell align="right"><strong>Day 7</strong></TableCell>
                      <TableCell align="right"><strong>Day 14</strong></TableCell>
                      <TableCell align="right"><strong>Day 30</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {retentionCohorts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No data available
                        </TableCell>
                      </TableRow>
                    ) : (
                      retentionCohorts.map((cohort, index) => (
                        <TableRow key={index}>
                          <TableCell><strong>{cohort.cohort}</strong></TableCell>
                          <TableCell 
                            align="right"
                            sx={{ backgroundColor: getRetentionColor(cohort.day_0) }}
                          >
                            {cohort.day_0}%
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ backgroundColor: getRetentionColor(cohort.day_1) }}
                          >
                            {cohort.day_1}%
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ backgroundColor: getRetentionColor(cohort.day_7) }}
                          >
                            {cohort.day_7}%
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ backgroundColor: getRetentionColor(cohort.day_14) }}
                          >
                            {cohort.day_14}%
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ backgroundColor: getRetentionColor(cohort.day_30) }}
                          >
                            {cohort.day_30}%
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          {/* Conversion Funnel */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
            <Paper sx={{ padding: 2 }}>
              <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Conversion Funnel
              </Typography>
              
              {/* Date Range Selector for Funnel */}
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', marginBottom: 3 }}>
                  {/* Quick period buttons: 7d, 30d, 45d */}
                  <ToggleButtonGroup
                    value={selectedFunnelQuickPeriod}
                    exclusive
                    onChange={(e, value) => {
                      if (value !== null) {
                        setSelectedFunnelQuickPeriod(value);
                        const end = new Date();
                        const start = new Date();
                        if (value === '7d') {
                          start.setDate(end.getDate() - 6);
                        } else if (value === '30d') {
                          start.setDate(end.getDate() - 29);
                        } else if (value === '45d') {
                          start.setDate(end.getDate() - 44);
                        }
                        setFunnelTempStartDate(start);
                        setFunnelTempEndDate(end);
                      } else {
                        setSelectedFunnelQuickPeriod(null);
                      }
                    }}
                    aria-label="funnel quick period selection"
                  >
                    <ToggleButton value="7d" aria-label="7 days">
                      7d
                    </ToggleButton>
                    <ToggleButton value="30d" aria-label="30 days">
                      30d
                    </ToggleButton>
                    <ToggleButton value="45d" aria-label="45 days">
                      45d
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  {/* Calendar date picker */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <DatePicker
                      label="Start Date"
                      value={funnelTempStartDate}
                      onChange={(newValue) => {
                        setFunnelTempStartDate(newValue);
                        setSelectedFunnelQuickPeriod(null);
                        if (funnelTempEndDate && newValue && newValue > funnelTempEndDate) {
                          notify('Start date must be before end date', { type: 'warning' });
                        }
                      }}
                      slotProps={{
                        textField: {
                          sx: { minWidth: 150 },
                          error: !!(funnelTempStartDate && funnelTempEndDate && funnelTempStartDate > funnelTempEndDate)
                        }
                      }}
                    />
                    <Typography>—</Typography>
                    <DatePicker
                      label="End Date"
                      value={funnelTempEndDate}
                      onChange={(newValue) => {
                        setFunnelTempEndDate(newValue);
                        setSelectedFunnelQuickPeriod(null);
                        if (funnelTempStartDate && newValue && funnelTempStartDate > newValue) {
                          notify('Start date must be before end date', { type: 'warning' });
                        }
                      }}
                      slotProps={{
                        textField: {
                          sx: { minWidth: 150 },
                          error: !!(funnelTempStartDate && funnelTempEndDate && funnelTempStartDate > funnelTempEndDate)
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (!funnelTempStartDate || !funnelTempEndDate) {
                          notify('Please select both start and end dates', { type: 'warning' });
                          return;
                        }
                        if (funnelTempStartDate > funnelTempEndDate) {
                          notify('Start date must be before end date', { type: 'warning' });
                          return;
                        }
                        const formatDateLocal = (date: Date): string => {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          return `${year}-${month}-${day}`;
                        };
                        const startDateStr = formatDateLocal(funnelTempStartDate);
                        const endDateStr = formatDateLocal(funnelTempEndDate);
                        setFunnelStartDate(startDateStr);
                        setFunnelEndDate(endDateStr);
                        setUseFunnelCustomDateRange(true);
                      }}
                      disabled={!funnelTempStartDate || !funnelTempEndDate || !!(funnelTempStartDate && funnelTempEndDate && funnelTempStartDate > funnelTempEndDate)}
                    >
                      Apply
                    </Button>
                    
                    {useFunnelCustomDateRange && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setUseFunnelCustomDateRange(false);
                          setFunnelStartDate('');
                          setFunnelEndDate('');
                          setFunnelTempStartDate(null);
                          setFunnelTempEndDate(null);
                          setSelectedFunnelQuickPeriod(null);
                        }}
                      >
                        Reset
                      </Button>
                    )}
                  </Box>
                </Box>
              </LocalizationProvider>
              
              {funnelData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={funnelData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      type="number"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.toLocaleString()}
                      hide={true}
                    />
                    <Tooltip 
                      formatter={(value: any, name: any, props: any) => [
                        `${value.toLocaleString()} users (${props.payload.percentage}%)`,
                        'Users'
                      ]}
                    />
                    <Bar 
                      dataKey="value" 
                      fill="#1976d2"
                      radius={[4, 4, 0, 0]}
                    >
                      <LabelList 
                        dataKey="value" 
                        position="top"
                        content={({ value, payload }: any) => (
                          <text x={0} y={0} dy={-4} fill="#666" fontSize={12} textAnchor="middle">
                            {`${value.toLocaleString()} (${payload?.percentage || 0}%)`}
                          </text>
                        )}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ padding: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No funnel data available</Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
          </Box>
        )}

        {/* Tab Panel : List of Users */}
        {tabValue === 2 && (
          <Box>
            {/*  Cards - Registered Users and Waitlist Users */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, marginBottom: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Registered Users
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: 1 }}>
                      {userStats.registered_users_count.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                      Verified users
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Waitlist Users
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: 1 }}>
                      {userStats.waitlist_users_count.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                      Users in waitlist
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Toggle and Users Table */}
            <Paper sx={{ padding: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <Typography variant="h6">
                  Users List
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isRegistered}
                      onChange={(e) => {
                        setIsRegistered(e.target.checked);
                        setUsersPage(0);
                      }}
                    />
                  }
                  label="Registered"
                />
              </Box>

              {usersLoading ? (
                <Loading />
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Name</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No users found
                            </TableCell>
                          </TableRow>
                        ) : (
                          users.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell>{user.name || '-'}</TableCell>
                              <TableCell>{user.email}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Pagination */}
                  <TablePagination
                    component="div"
                    count={usersTotal}
                    page={usersPage}
                    onPageChange={(event, newPage) => setUsersPage(newPage)}
                    rowsPerPage={usersRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setUsersRowsPerPage(parseInt(event.target.value, 10));
                      setUsersPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                    labelRowsPerPage="Rows per page:"
                  />
                </>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </List>
  );
};

export default UserAnalyticsList;

